import React, { use, useCallback } from "react";
import { useTheme } from "../../hooks/use-theme";
import { useI18n } from "../../hooks/i18n-hooks";
import TabBar from "../../components/TabBar";
import UserActivitiesNavigation from "../user/ActivitiesNavigation";
import Icon from "../../components/Icon";
import ClientWorkersNavigation from "./WorkersNavigation";
import UserMissionsNavigation, { UserMissionsTabParamList } from "../user/UserMissionsNavigation";
import { useNavigationNavigator } from "../useNavigationNavigator";
import { EventArg, NavigatorScreenParams } from "@react-navigation/native";
import InvoicesScreen from "../../screens/client/InvoiceList";
import IntercomScreen from "../../screens/user/Intercom";
import UserAvatar from "../../screens/user/UserAvatar";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { useIntercom } from "../../hooks/intercom";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { ClientProfile } from "../../__generated__/graphql";
import { AuthManagerContext } from "../../contexts/AuthManager";
import ProfileStack from "../ProfileStack";
import { useTracking } from "../../hooks/tracking";
import ScreenHeader from "../../screens/user/ScreenHeader";

export type ClientTabParamList = {
    MissionsScreen: NavigatorScreenParams<UserMissionsTabParamList>;
    WorkersScreen: undefined;
    ActivitiesTab: undefined;
    InvoicesTab: undefined;
    Intercom: undefined;
    ProfileStack: undefined;
};

const ClientHomeScreen = () => {
    const { colors } = useTheme();
    const i18n = useI18n();
    const { presentIntercom } = useIntercom();
    const { openProfileSwitch, unreadProfilesActivities, profileId } = use(
        ProfileSwitcherManagerContext,
    );
    const { user } = use(AuthManagerContext);
    const { NavigationNavigator, navigatorProps } = useNavigationNavigator();
    const { trackEvent } = useTracking();
    const clientProfile = useCurrentProfile<ClientProfile>();

    const listeners = useCallback(
        () => ({
            tabLongPress: openProfileSwitch,
        }),
        [openProfileSwitch],
    );

    const intercomScreenListener = useCallback(
        () => ({
            tabPress: (e: EventArg<"tabPress", true>) => {
                e.preventDefault();
                void trackEvent("click_show_help");
                void presentIntercom();
            },
        }),
        [presentIntercom, trackEvent],
    );

    return (
        <NavigationNavigator.Navigator
            {...navigatorProps}
            screenOptions={{
                ...navigatorProps.screenOptions,
                lazy: false,
                freezeOnBlur: false,
            }}
            initialRouteName="MissionsScreen"
            tabBar={(props) => <TabBar {...props} />}
        >
            <NavigationNavigator.Screen
                name="MissionsScreen"
                component={UserMissionsNavigation}
                options={{
                    tabBarIcon: (props) => <Icon {...props} name="mission" />,
                    title: i18n.t("screens.client_profile.tabs.missions.title"),
                    headerShown: false,
                }}
            />
            <NavigationNavigator.Screen
                name="WorkersScreen"
                component={ClientWorkersNavigation}
                options={{
                    tabBarIcon: (props) => <Icon {...props} name="talents" />,
                    headerShown: false,
                    title: i18n.t("screens.client_profile.tabs.employees.title"),
                }}
            />
            <NavigationNavigator.Screen
                name="InvoicesTab"
                component={InvoicesScreen}
                options={{
                    tabBarIcon: (props) => <Icon {...props} name="invoice" />,
                    title: i18n.t("screens.user.tabs.invoices"),
                    header: () => {
                        return <ScreenHeader label={i18n.t("screens.user.tabs.invoices")} />;
                    },
                }}
            />
            <NavigationNavigator.Screen
                name="Intercom"
                component={IntercomScreen} // keep this screen if a navigate() goes there
                options={{
                    tabBarIcon: (props) => <Icon {...props} name="circle-question" />,
                    title: i18n.t("screens.client_profile.tabs.help.title"),
                }}
                listeners={intercomScreenListener}
            />
            <NavigationNavigator.Screen
                name="ActivitiesTab"
                component={UserActivitiesNavigation}
                options={{
                    tabBarIcon: (props) => <Icon {...props} name="todo" />,
                    headerShown: false,
                    title: i18n.t("screens.user.tabs.todos"),
                    tabBarBadge: profileId ? unreadProfilesActivities[profileId] : 0,
                    headerStyle: { backgroundColor: colors.surface },
                }}
            />
            <NavigationNavigator.Screen
                name="ProfileStack"
                component={ProfileStack}
                options={{
                    title: i18n.t("screens.client_profile.tabs.profile.title"),
                    headerShown: false,
                    tabBarIcon: () => (
                        <UserAvatar
                            size={30}
                            avatarPath={clientProfile?.client.logoUrl || user?.avatarUrl}
                            canEnlarge={false}
                        />
                    ),
                }}
                listeners={listeners}
            />
        </NavigationNavigator.Navigator>
    );
};

export default ClientHomeScreen;
